// File: e1c/src/components/content_editor/PatientQuestionRenderer.js
// HOLISTIC REFACTOR V4.2: Fixes FormGroup import, consumes standardized block structure.

import React from 'react';
import { 
    Box, Typography, RadioGroup, FormControlLabel, Radio, TextField, 
    Paper, Checkbox, Slider, FormControl, FormGroup, Grid, Card
} from '@mui/material';
import { BLOCK_TYPES } from 'constants/contentBlockTypes';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

const PatientQuestionRenderer = ({ questionDefinition, mode = 'preview' }) => {
    if (!questionDefinition || typeof questionDefinition.type !== 'string' || !questionDefinition.question) {
        // It's better to return a minimal Paper for layout consistency in VisualContentEditor
        return (
            <Paper elevation={0} sx={{ p: 2, my: 1, border: '1px dashed #e0e0e0', borderRadius: '4px', backgroundColor: '#fafafa' }}>
                <Typography variant="caption" color="textSecondary" component="div">
                    Preview unavailable: Essential question data (like type or text) is missing.
                </Typography>
            </Paper>
        );
    }

    const {
        question: question_text, // 'question' from the block object
        type: block_type_from_prop,  // 'type' from the block object
        options                   // 'options' object from the block
    } = questionDefinition;

     const blockOptions = (typeof options === 'object' && options !== null) ? options : {};
    // Add this line inside the component function, after the 'blockOptions' variable
    
    const mediaAssets = (Array.isArray(blockOptions.media_assets) && blockOptions.media_assets) || [];

    const typeForSwitch = typeof block_type_from_prop === 'string' ? block_type_from_prop.trim() : 'UNKNOWN';

    const renderQuestionContent = () => {
        switch (typeForSwitch) {
            case BLOCK_TYPES.YES_NO:
                return (
                    <FormControl component="fieldset">
                        <RadioGroup row name={`preview_yes_no_${questionDefinition.id || 'temp'}`} defaultValue="">
                            <FormControlLabel value={blockOptions.yesValue || "yes"} control={<Radio disabled={mode === 'preview'} size="small" />} label={blockOptions.yesLabel || "Yes"} />
                            <FormControlLabel value={blockOptions.noValue || "no"} control={<Radio disabled={mode === 'preview'} size="small" />} label={blockOptions.noLabel || "No"} />
                        </RadioGroup>
                    </FormControl>
                );

            case BLOCK_TYPES.OPEN_TEXT:
                return (
                    <TextField
                        fullWidth
                        multiline
                        rows={blockOptions.rows || 3}
                        variant="outlined"
                        placeholder={blockOptions.placeholder || "Patient answer goes here..."}
                        disabled={mode === 'preview'}
                        InputProps={{ readOnly: mode === 'preview' }}
                        sx={{backgroundColor: mode === 'preview' ? '#f5f5f5' : 'inherit' }}
                    />
                );
            
            case BLOCK_TYPES.SINGLE_CHOICE_SELECT:
                 const singleChoices = Array.isArray(blockOptions.choices) ? blockOptions.choices : [];
                 if (singleChoices.length === 0 && mode === 'preview') { // Show message only in preview if no choices
                    return <Typography variant="caption" color="textSecondary">No choices defined for single-select.</Typography>;
                 }
                 return (
                    <FormControl component="fieldset">
                        <RadioGroup name={`preview_single_choice_${questionDefinition.id || 'temp'}`} defaultValue="">
                            {singleChoices.map((opt, index) => (
                                <FormControlLabel 
                                    key={opt.value || `preview-sc-${index}`} 
                                    value={String(opt.value)} 
                                    control={<Radio disabled={mode === 'preview'} size="small" />} 
                                    label={opt.label || `Option ${index + 1}`} 
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                );

            case BLOCK_TYPES.MULTI_CHOICE_SELECT:
                const multiChoices = Array.isArray(blockOptions.choices) ? blockOptions.choices : [];
                if (multiChoices.length === 0 && mode === 'preview') {
                    return <Typography variant="caption" color="textSecondary">No choices defined for multi-select.</Typography>;
                }
                return (
                    <FormControl component="fieldset">
                        <FormGroup> {/* Use FormGroup for Checkboxes */}
                            {multiChoices.map((opt, index) => (
                                <FormControlLabel
                                    key={opt.value || `preview-mc-${index}`}
                                    control={<Checkbox disabled={mode === 'preview'} size="small" />}
                                    label={opt.label || `Option ${index + 1}`}
                                />
                            ))}
                        </FormGroup>
                    </FormControl>
                );

            case BLOCK_TYPES.SCALE:
                const min = parseFloat(blockOptions.min);
                const max = parseFloat(blockOptions.max);
                const step = parseFloat(blockOptions.step || 1); // Default step to 1
                const minLabel = blockOptions.minLabel || (Number.isFinite(min) ? String(min) : "Min");
                const maxLabel = blockOptions.maxLabel || (Number.isFinite(max) ? String(max) : "Max");

                if (!Number.isFinite(min) || !Number.isFinite(max) || min >= max) {
                    return <Typography variant="caption" color="error">Scale error (min/max). Check options. Min: '{blockOptions.min}', Max: '{blockOptions.max}'</Typography>;
                }

                return (
                    <Box sx={{ px: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb:0.5 }}>
                             <Typography variant="body2" sx={{ textAlign: 'left'}}>{minLabel}</Typography>
                             <Typography variant="body2" sx={{ textAlign: 'right'}}>{maxLabel}</Typography>
                        </Box>
                        <Slider
                            defaultValue={min}
                            value={mode === 'preview' ? min : undefined} // Display at min for preview
                            min={min}
                            max={max}
                            step={step}
                            valueLabelDisplay="off" // Usually off for preview, can be 'auto'
                            disabled={mode === 'preview'}
                            marks={[{value: min, label:''}, {value: max, label:''}]}
                        />
                    </Box>
                );
            
            case BLOCK_TYPES.NUMBER:
                return (
                    <TextField
                        type="number"
                        label={blockOptions.unit ? `Value (${blockOptions.unit})` : "Number Input"}
                        placeholder={blockOptions.placeholder || "Enter a number"}
                        disabled={mode === 'preview'}
                        variant="outlined"
                        size="small"
                        InputProps={{
                            readOnly: mode === 'preview',
                            inputProps: {
                                min: blockOptions.min,
                                max: blockOptions.max,
                                step: blockOptions.step,
                            },
                            endAdornment: blockOptions.unit ? <Typography variant="body2" sx={{mr:1}}>{blockOptions.unit}</Typography> : null,
                            sx: {backgroundColor: mode === 'preview' ? '#f5f5f5' : 'inherit' }
                        }}
                        // sx={{maxWidth: '200px'}} // Optional: constrain width
                    />
                );
            
            case BLOCK_TYPES.INFO_TEXT:
               
                
                return (
                    <Typography variant="body1" sx={{whiteSpace: 'pre-wrap', color: 'text.secondary'}}>
                        {question_text} {/* For INFO_TEXT, 'question' field holds the content */}
                    </Typography>
                );
            
            case BLOCK_TYPES.STANDARDIZED_ASSESSMENT: // <-- ADD THIS CASE
                const assessmentName = blockOptions.assessment_key ? `(${blockOptions.assessment_key})` : '(Not Yet Selected)';
                return (
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
                        Patient will be presented with the full interactive '{assessmentName}' assessment.
                        <br/>
                        (Full preview not available in this view).
                    </Typography>
                );

            default:
                return <Typography variant="caption" color="textSecondary">Preview not available for block type: "{block_type_from_prop || 'Unknown'}".</Typography>;
        }


    };

    return (
        <Paper elevation={0} sx={{ p: 2, my: 1, border: '1px dashed #ccc', borderRadius: '4px' }}>
            <Typography variant="subtitle1" component="div" gutterBottom sx={{ fontWeight: 'medium', color: mode === 'preview' ? 'text.primary' : 'inherit' }}>
                {question_text || (block_type_from_prop === BLOCK_TYPES.INFO_TEXT ? "(Informational Text Block)" : "(Question text not set)")}
            </Typography>

            {mediaAssets.length > 0 && (
            <Box sx={{ my: 2 }}>
                <Grid container spacing={1}>
                    {mediaAssets.map((asset, index) => {
                        const apiUrlBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

                        const imageUrl = (asset.media_type === 'image' && asset.url)
                            ? (asset.url.startsWith('http') ? asset.url : `${apiUrlBase}${asset.url}`)
                            : null;

                        const videoThumbnailUrl = (asset.media_type === 'video' && asset.thumbnail_url)
                            ? (asset.thumbnail_url.startsWith('http') ? asset.thumbnail_url : `${apiUrlBase}${asset.thumbnail_url}`)
                            : null;

                        return (
                            <Grid item xs={12} sm={mediaAssets.length > 1 ? 6 : 12} key={asset.id || index}>
                                <Card variant="outlined">
                                    {(() => {
                                        if (asset.media_type === 'image' && imageUrl) {
                                            return (
                                                <Box
                                                    component="img"
                                                    src={imageUrl}
                                                    alt={`Attachment ${index + 1}`}
                                                    sx={{ width: '100%', height: 'auto', display: 'block' }}
                                                />
                                            );
                                        } 
                                        else if (asset.media_type === 'video' && videoThumbnailUrl) {
                                            return (
                                                <Box sx={{ position: 'relative', width: '100%', background: '#000' }}>
                                                    <Box
                                                        component="img"
                                                        src={videoThumbnailUrl}
                                                        alt={`Video Thumbnail ${index + 1}`}
                                                        sx={{ 
                                                            width: '100%', 
                                                            height: 'auto', 
                                                            display: 'block',
                                                            opacity: 0.7 
                                                        }}
                                                    />
                                                    <PlayCircleOutlineIcon 
                                                        sx={{
                                                            position: 'absolute',
                                                            top: '50%',
                                                            left: '50%',
                                                            transform: 'translate(-50%, -50%)',
                                                            fontSize: '4rem',
                                                            color: 'white',
                                                            pointerEvents: 'none'
                                                        }}
                                                    />
                                                </Box>
                                            );
                                        } 
                                        else {
                                            return (
                                                <Box sx={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1, backgroundColor: 'grey.100' }}>
                                                    <Typography variant="caption" color="text.secondary" textAlign="center">
                                                        Preview not available.
                                                    </Typography>
                                                </Box>
                                            );
                                        }
                                    })()}
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Box>
                )}

            <Box mt={1.5}>
                {renderQuestionContent()}
            </Box>
            {mode === 'preview' && (
                 <Typography variant="caption" display="block" sx={{mt: 1.5, fontStyle: 'italic', color: 'text.secondary', textAlign:'center'}}>
                    Patient View Preview
                 </Typography>
            )}
        </Paper>
    );
};

export default PatientQuestionRenderer;